import { APP_INITIALIZER, inject, isDevMode, Provider } from '@angular/core';
import { provideTransloco, TranslocoService, TranslocoTestingOptions } from '@jsverse/transloco';
import { TransLocoHttpLoaderUtilityClass } from './transloco-utilities/transloco.utils';

const LANGUAGE_STORAGE_KEY = 'translocoLang';

/**
 * Factory function to initialize language from localStorage on app startup.
 */
function initializeLanguage(): () => void {
  return () => {
    const translocoService = inject(TranslocoService);
    const savedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLang && ['en', 'da', 'sv', 'no', 'es'].includes(savedLang)) {
      translocoService.setActiveLang(savedLang);
    }
  };
}

/**
 * Provides a Transloco configuration with specific settings and language options.
 *
 * This function initializes the Transloco service with the given configuration.
 * It sets up available languages, default language, and other settings such as re-rendering on language change
 * and controlling production mode behavior. Language selection is persisted to localStorage.
 *
 * @returns {Provider[]} An array of configured Provider instances for the Transloco service with persistence.
 */
export const provideTransLocoTranslate = (): Provider[] => {
  return [
    provideTransloco({
      config: {
        availableLangs: ['en', 'da', 'sv', 'no', 'es'],
        defaultLang: 'da',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TransLocoHttpLoaderUtilityClass,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      multi: true,
    },
  ];
};

/**
 * Sets up and provides Transloco testing configuration with default options for use in test environments.
 *
 * @param {TranslocoTestingOptions} [options] Optional configuration object to override default Transloco options.
 * @return Provider of {TranslocoTestingOptions} The configured Transloco testing provider options.
 */
export function provideTransLocoTesting(options?: TranslocoTestingOptions): Provider {
  return provideTransloco({
    config: {
      availableLangs: ['en', 'da', 'sv', 'no', 'es'],
      defaultLang: 'en',
      reRenderOnLangChange: true,
      prodMode: false,
    },
    loader: TransLocoHttpLoaderUtilityClass,
    ...options,
  });
}
